import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

/**
 * Systematische interne Verlinkung der SEO-Cluster (SEO-Auftrag Block 3.3).
 * Regel pro Länderseite: Hub-Link + 2 andere Länder desselben Clusters +
 * Querlink zum anderen Cluster im selben Land. Ankertexte immer mit Keyword.
 */
const CLUSTERS = {
  steuerruecklagen: {
    hub: { url: '/steuerruecklagen-freelancer', anchor: 'Steuerrücklagen-Guide für DE, AT & CH' },
    countries: {
      DE: { url: '/steuerruecklagen-freelancer-deutschland', anchor: 'Steuerrücklagen in Deutschland berechnen' },
      AT: { url: '/steuerruecklagen-freelancer-oesterreich', anchor: 'Steuerrücklagen in Österreich berechnen' },
      CH: { url: '/steuerruecklagen-freelancer-schweiz', anchor: 'Steuerrücklagen in der Schweiz planen' },
    },
  },
  buchhaltung: {
    hub: null,
    countries: {
      DE: { url: '/buchhaltung-freelancer-deutschland', anchor: 'Buchhaltung für Freelancer in Deutschland' },
      AT: { url: '/buchhaltung-freelancer-oesterreich', anchor: 'Buchhaltung für Freelancer in Österreich' },
      CH: { url: '/buchhaltung-freelancer-schweiz', anchor: 'Buchhaltung für Selbständige in der Schweiz' },
    },
  },
  rechnung: {
    hub: null,
    countries: {
      DE: { url: '/rechnung-erstellen-kostenlos-deutschland', anchor: 'Rechnung erstellen für Deutschland (kostenlos)' },
      AT: { url: '/rechnung-erstellen-kostenlos-oesterreich', anchor: 'Rechnung erstellen für Österreich (kostenlos)' },
      CH: { url: '/qr-rechnung-erstellen-kostenlos', anchor: 'QR-Rechnung für die Schweiz erstellen (kostenlos)' },
    },
  },
};

const COUNTRY_LABEL = { DE: 'Deutschland', AT: 'Österreich', CH: 'Schweiz' };

const LinkRow = ({ to, anchor, testid }) => (
  <li>
    <Link
      to={to}
      data-testid={testid}
      className="group inline-flex items-start gap-2 text-emerald-700 hover:text-emerald-900 font-medium"
    >
      <ArrowRight className="h-4 w-4 mt-1 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
      <span className="underline decoration-emerald-300 underline-offset-4">{anchor}</span>
    </Link>
  </li>
);

/**
 * @param {string} cluster  'steuerruecklagen' | 'buchhaltung' | 'rechnung'
 * @param {string} country  'DE' | 'AT' | 'CH' | null (null = Hub-Seite)
 */
export const SeoClusterLinks = ({ cluster, country = null }) => {
  const clusterDef = CLUSTERS[cluster];
  if (!clusterDef) return null;

  const links = [];
  if (country) {
    // Länderseite: zurück zum Hub
    if (clusterDef.hub) {
      links.push({ ...clusterDef.hub, testid: `cluster-link-hub-${cluster}` });
    }
    // Die zwei anderen Länderseiten desselben Clusters
    Object.entries(clusterDef.countries)
      .filter(([c]) => c !== country)
      .forEach(([c, l]) => links.push({ ...l, testid: `cluster-link-${cluster}-${c}` }));
    // Querlinks zu den anderen Clustern im selben Land
    Object.entries(CLUSTERS)
      .filter(([key]) => key !== cluster)
      .forEach(([key, def]) => {
        const l = def.countries[country];
        if (l) links.push({ ...l, testid: `cluster-link-cross-${key}-${country}` });
      });
  } else {
    // Hub-Seite: alle Länderseiten des eigenen Clusters + anderer Hub
    Object.entries(clusterDef.countries).forEach(([c, l]) =>
      links.push({ ...l, testid: `cluster-link-${cluster}-${c}` })
    );
    Object.entries(CLUSTERS)
      .filter(([key, def]) => key !== cluster && def.hub)
      .forEach(([key, def]) => links.push({ ...def.hub, testid: `cluster-link-hub-${key}` }));
  }

  if (!links.length) return null;

  return (
    <section className="py-12 bg-gray-50 border-t border-gray-100" data-testid={`seo-cluster-links-${cluster}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-xl font-bold text-gray-900 mb-5">
          {country ? `Weiterführende Guides & Rechner${COUNTRY_LABEL[country] ? ` für ${COUNTRY_LABEL[country]} & DACH` : ''}` : 'Die Länder-Guides im Detail'}
        </h2>
        <ul className="space-y-3">
          {links.map((l) => (
            <LinkRow key={l.url} to={l.url} anchor={l.anchor} testid={l.testid} />
          ))}
        </ul>
      </div>
    </section>
  );
};

export default SeoClusterLinks;
